import { createContext, useContext, useState } from "react";
import type { LocalClientInfo } from "../lib/api";
import { Button, Field } from "../components/rumonai";
import { PageHead } from "../shell/PageHead";
import { LoadingState } from "../shell/ui";
import { LocalInstaller } from "./LocalInstaller";
import { clientErrorText, type LocalClient } from "./api";
import "../connections/settings-list.css";
import "./clients.css";

export interface ClientDirectory {
  clients: LocalClient[] | null;
  error: unknown;
  refresh: () => void;
}
export const LocalClientsContext = createContext<ClientDirectory>({ clients: null, error: null, refresh: () => {} });
export const useLocalClients = () => useContext(LocalClientsContext);

export function clientName(client: Pick<LocalClientInfo, "id" | "name">, clients: readonly LocalClientInfo[] = []): string {
  const name = client.name || "电脑";
  return clients.filter(item => item.name === client.name).length > 1 ? name + " · " + client.id.slice(-6) : name;
}

export function LocalClientSetup() {
  const [expanded, setExpanded] = useState(false);
  const [link, setLink] = useState("");
  const [error, setError] = useState<string | null>(null);
  function continueConnection() {
    try {
      const url = new URL(link.trim());
      const id = url.hash.match(/^#\/connect\?client=([A-Za-z0-9_-]{1,128})$/)?.[1];
      if (url.origin !== window.location.origin || url.username || url.password || url.pathname !== "/" || url.search || !id)
        throw new Error("invalid confirmation link");
      setError(null);
      window.location.hash = "#/connect?client=" + id;
    } catch {
      setError("请粘贴此网站的客户端确认链接。");
    }
  }
  return <div className="client-setup">
    <Button type="button" aria-expanded={expanded} onClick={() => setExpanded(value => !value)}>连接客户端</Button>
    {expanded ? <div className="client-connect-guide">
      <ol>
        <li>打开已安装的客户端，点“连接网页”或“重新授权账号”。</li>
        <li>复制客户端显示的确认地址。</li>
        <li>在下方粘贴确认链接，点“继续”。</li>
      </ol>
      <Field label="确认链接" error={error}>
        <input type="url" value={link} placeholder="粘贴客户端复制的确认链接" autoComplete="off" spellCheck={false}
          onChange={event => { setLink(event.target.value); setError(null); }}
          onKeyDown={event => { if (event.key === "Enter") { event.preventDefault(); continueConnection(); } }} />
      </Field>
      <Button type="button" disabled={!link.trim()} onClick={continueConnection}>继续</Button>
    </div> : null}
    <LocalInstaller />
  </div>;
}

export function LocalClientsPage() {
  const { clients, error, refresh } = useLocalClients();
  const [adding, setAdding] = useState(false);
  const selectedId = new URLSearchParams(window.location.hash.split("?")[1]).get("client");
  return <div className="owner-page local-clients">
    <a className="agent-back" href="/#/settings?section=workspace">返回账号设置</a>
    <PageHead title="我的电脑" actions={clients?.length ? <Button variant="secondary"
      aria-expanded={adding} onClick={() => setAdding(value => !value)}>{adding ? "收起" : "连接电脑"}</Button> : null} />
    {error ? <div className="settings-actions" role="alert"><span>{clientErrorText(error)}</span>
      <Button variant="secondary" onClick={refresh}>重试</Button></div> : null}
    {clients === null && !error ? <LoadingState label="正在读取我的电脑…" /> : null}
    {clients?.length ? <div className="settings-list" aria-label="已连接账号的电脑">
      {clients.map(client => <section className="settings-row client-row" key={client.id} data-selected={selectedId === client.id || undefined}>
        <div className="settings-row-main">
          <h2 className="settings-row-title" data-quoted="true">{clientName(client, clients)}</h2>
          <p className="settings-row-desc client-workspace">工作空间：<span data-quoted="true">{client.workspace}</span></p>
          <span className="settings-row-desc">{client.agent_count} 个 Agent</span>
          {client.error ? <span className="settings-row-desc client-error" role="alert" data-quoted="true" title={client.error}>{client.error}</span> : null}
        </div>
        <div className="settings-row-side"><span className="client-presence">
          <span className={"settings-dot" + (client.online ? " is-good" : "")} aria-hidden="true" />{error ? "状态待更新" : client.online ? "在线" : "离线"}
        </span><a className="btn btn-quiet" href={"/#/connect?client_id=" + encodeURIComponent(client.id)}>在此电脑新建 IP</a></div>
      </section>)}
    </div> : null}
    {clients?.length === 0 && !error ? <section className="settings-group"><h2 className="settings-group-title">连接自己的电脑</h2><LocalClientSetup /></section>
      : adding ? <section className="settings-group"><h2 className="settings-group-title">连接另一台电脑</h2><LocalClientSetup /></section> : null}
  </div>;
}
